import React from "react";
import t from "../../../../i18n";
import ModalForm from "@ui/Modal/Form";
import type { Project } from "../../../../api/useProjects";
import Select from "@ui/Select";
import useInput from "@ui/lib/hooks/useInput";
import { comparatorBy } from "diagnostics-lib/comparator";
import AdditionnalMessage from "@ui/AdditionnalMessage";
import useActiveAnalysesStream from "../../../../api/useActiveAnalysesStream";
import { indexBy } from "diagnostics-lib/iterable";
import { usePost } from "../../../../api/useCall";

interface P {
  project: Project;
  projectsWithIdenticalSource: Project[];
  onRequestClose: () => void;
}
const CopyingProjectAnalysesModalForm = ({
  project,
  projectsWithIdenticalSource,
  onRequestClose,
}: P) => {
  const copyActivation = usePost("/projects/:projectId(uuid)/analyses/activation/copy");

  const projectIndexById = indexBy(projectsWithIdenticalSource, "id");
  const projectOptions = React.useMemo(
    () =>
      projectsWithIdenticalSource
        .map(m => ({ label: m.name, value: m.id }))
        .sort(comparatorBy("label")),
    [projectsWithIdenticalSource],
  );
  const { value: targetProjectId, onChange: onChangeTargetProjectId } = useInput(
    projectOptions[0]?.value,
  );

  const { activeAnalyses } = useActiveAnalysesStream(
    projectIndexById.getOrThrow(targetProjectId).id,
  );

  const handleSubmit = React.useCallback(async () => {
    try {
      await copyActivation({
        routeParams: { projectId: project.id },
        query: { targetProjectId, dryRun: false },
      });
    } catch (error) {
      return false;
    }

    return true;
  }, [copyActivation, project.id, targetProjectId]);

  return (
    <ModalForm
      onSubmit={activeAnalyses.length > 0 ? handleSubmit : false}
      onRequestClose={onRequestClose}
      okButtonName={t("project.modal_form.copying_project_analyses.ok_button_name")}
    >
      <Select
        filtered
        prompt=""
        label={t("project.modal_form.copying_project_analyses.select_label.project")}
        value={targetProjectId}
        onChange={onChangeTargetProjectId}
        options={projectOptions}
        clearable={false}
      />
      <AdditionnalMessage
        status="INFO"
        text={t(
          "project.modal_form.copying_project_analyses.additionnal_message.info",
          activeAnalyses.length,
        )}
      />
    </ModalForm>
  );
};
export default React.memo(CopyingProjectAnalysesModalForm);
